import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText, 
  Paper, 
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import { 
  Event as EventIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Image as ImageIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { collection, addDoc, doc, writeBatch, arrayUnion, serverTimestamp, Timestamp, getDoc, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db } from '../../../firebase/config';
import TicketTypeManager, { TicketType } from './components/TicketTypeManager';
import { useUser } from '../../../contexts/UserContext';
import EventEmbedGenerator from '../../../components/events/EventEmbedGenerator';
import { toast } from 'react-toastify';

const eventTypes = [
  'Conference',
  'Workshop',
  'Seminar',
  'Networking',
  'Product Launch',
  'Training',
  'Meetup',
  'Other'
];

interface EventFormValues {
   title: string;
   description: string;
   eventType: string;
   isPublic: boolean;
   location: string;
   startDate: Date;
   endDate: Date | null;
   isPaid: boolean;
   maxCapacity: number;
   coverPhoto: File | null;
   enableParticipantSharing: boolean;
}

const CreateOrgEvent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { currentUser } = useUser();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(null);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [eventId, setEventId] = useState<string | null>(null);
  
  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
    eventType: Yup.string().required('Event type is required'),
    isPublic: Yup.boolean(),
    location: Yup.string().required('Location is required'),
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().nullable().min(Yup.ref('startDate'), 'End date cannot be before start date'),
    isPaid: Yup.boolean(),
    maxCapacity: Yup.number()
      .required('Max capacity is required')
      .min(1, 'Capacity must be at least 1')
  });
  
  const formik = useFormik<EventFormValues>({
     initialValues: {
        title: '',
        description: '',
        eventType: '',
        isPublic: true,
        location: '',
        startDate: new Date(),
        endDate: null,
        isPaid: false,
        maxCapacity: 100,
        coverPhoto: null,
        enableParticipantSharing: true,
     },
     validationSchema,
     onSubmit: async (values) => {
        if (!id || !currentUser) {
           setError('Missing organization ID or user information');
           return;
        }

        // Prevent duplicate submissions
        if (loading) {
           return;
        }

        try {
           setLoading(true);
           setError(null);

           // Check if at least one ticket type is defined when event is paid
           const hasPaidTickets = ticketTypes.some((ticket) => ticket.price > 0);
           if (values.isPaid && ticketTypes.length === 0) {
              setError('Please add at least one ticket type for a paid event');
              setLoading(false);
              return;
           }

           // Upload cover photo if provided
           let coverImageUrl = '';
           if (values.coverPhoto) {
              const storage = getStorage();
              const storageRef = ref(
                 storage,
                 `organizations/${id}/eventCovers/${Date.now()}_${values.coverPhoto.name}`
              );
              const uploadTask = await uploadBytesResumable(storageRef, values.coverPhoto);
              coverImageUrl = await getDownloadURL(uploadTask.ref);
           }

           // Create a unique ID for the event
           const eventRef = doc(collection(db, 'events'));
           const newEventId = eventRef.id;
           setEventId(newEventId);

           // Base event data that would be consistent
           const eventData: {
              id: string;
              title: string;
              description: string;
              eventType: string;
              isPublic: boolean;
              location: string;
              startDate: Timestamp;
              endDate: Timestamp | null;
              isPaid: boolean;
              maxCapacity: number;
              organizationId: string;
              userId: string;
              createdBy: string;
              createdAt: any;
              updatedAt: any;
              ticketsSold: number;
              revenue: number;
              coverPhotoUrl: string;
              participants: any[];
              ticketTypes?: TicketType[];
              enableParticipantSharing: boolean;
           } = {
              id: newEventId, // Add ID directly in the document
              title: values.title,
              description: values.description,
              eventType: values.eventType,
              isPublic: values.isPublic,
              // Handle location as string for consistent querying
              location: values.location,
              // Ensure proper timestamp format
              startDate: Timestamp.fromDate(values.startDate),
              endDate: values.endDate ? Timestamp.fromDate(values.endDate) : null,
              isPaid: values.isPaid || hasPaidTickets,
              maxCapacity: values.maxCapacity,
              organizationId: id,
              // Make sure to add userId consistently
              userId: currentUser.uid,
              createdBy: currentUser.uid,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              ticketsSold: 0,
              revenue: 0,
              coverPhotoUrl: coverImageUrl || '',
              // Default participant - owner
              participants: [
                 {
                    userId: currentUser.uid,
                    email: currentUser.email || '',
                    displayName: currentUser.displayName || 'Event Creator',
                    photoURL: currentUser.photoURL || '',
                    role: 'owner',
                    status: 'accepted',
                    invitedAt: new Date(),
                    invitedBy: currentUser.uid,
                 },
              ],
              enableParticipantSharing: values.enableParticipantSharing,
           };

           // Add ticket types if available
           if (ticketTypes.length > 0) {
              eventData.ticketTypes = ticketTypes;
           } else {
              // Create default ticket if none specified
              eventData.ticketTypes = [
                 {
                    id: 'default',
                    name: 'General Admission',
                    description: 'Standard event ticket',
                    price: 0,
                    capacity: values.maxCapacity,
                    ticketsSold: 0,
                    isDefault: true,
                 },
              ];
           }

           // Create a transaction to ensure data consistency
           const batch = writeBatch(db);

           // Convert dates to Firestore timestamps
           const eventDataForFirestore = {
              ...eventData,
              startDate: Timestamp.fromDate(values.startDate),
              endDate: values.endDate ? Timestamp.fromDate(values.endDate) : null,
              createdAt: Timestamp.fromDate(new Date()),
              updatedAt: serverTimestamp(),
              // Ensure participants have the correct format
              participants: eventData.participants.map((p) => ({
                 ...p,
                 invitedAt: Timestamp.fromDate(p.invitedAt),
              })),
           };

           // Set the event document
           batch.set(eventRef, eventDataForFirestore);

           // Also update the organization's events array
           const orgRef = doc(db, 'organizations', id);

           // Check if organization exists first
           const orgDoc = await getDoc(orgRef);
           if (!orgDoc.exists()) {
              setError('Organization not found');
              setLoading(false);
              return;
           }

           batch.update(orgRef, {
              events: arrayUnion(newEventId),
              updatedAt: serverTimestamp(),
           });

           // Commit the transaction
           await batch.commit();

           // Create activity record for this event
           try {
              await addDoc(collection(db, 'organizationActivities'), {
                 organizationId: id,
                 userId: currentUser.uid,
                 userName: currentUser.displayName || 'User',
                 userAvatar: currentUser.photoURL || '',
                 action: 'created',
                 entityType: 'event',
                 entityId: newEventId,
                 entityName: values.title,
                 timestamp: serverTimestamp(),
                 meta: {
                    eventType: values.eventType,
                    isPublic: values.isPublic,
                 },
              });
           } catch (activityErr) {
              console.error('Failed to record activity:', activityErr);
              // Non-critical error, continue
           }

           setLoading(false);
           toast.success('Event created successfully!');
           navigate(`/organizations/${id}/events/${newEventId}`);
        } catch (err) {
           console.error('Error creating event:', err);
           setError('Failed to create event. Please try again.');
           setLoading(false);
        }
     },
  });
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      formik.setFieldValue('coverPhoto', file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTicketTypesChange = (updatedTicketTypes: TicketType[]) => {
    setTicketTypes(updatedTicketTypes);
    // Update isPaid if any ticket has a price > 0
    const hasPaidTickets = updatedTicketTypes.some(ticket => ticket.price > 0);
    if (hasPaidTickets && !formik.values.isPaid) {
      formik.setFieldValue('isPaid', true);
    }
  };
  
  return (
     <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Container maxWidth='md' sx={{ py: 2 }}>
           <Paper sx={{ p: 2, borderRadius: 1, boxShadow: 1 }}>
              <Box sx={{ mb: 3 }}>
                 <Typography variant='h5' component='h1' gutterBottom sx={{ fontWeight: 500 }}>
                    Create Event for {activeOrganization?.name}
                 </Typography>
                 <Typography variant='body2' color='text.secondary'>
                    Fill in the details to create a new event for your organization
                 </Typography>
              </Box>

              {error && (
                 <Alert severity='error' sx={{ mb: 3 }}>
                    {error}
                 </Alert>
              )}

              <form onSubmit={formik.handleSubmit}>
                 {/* Basic Information Section */}
                 <Typography
                    variant='subtitle1'
                    sx={{ mt: 2, mb: 1.5, fontWeight: 500 }}
                    component='h2'
                 >
                    <EventIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Basic Information
                 </Typography>
                 <Grid container spacing={2}>
                    <Grid item xs={12}>
                       <TextField
                          fullWidth
                          id='title'
                          name='title'
                          label='Event Title'
                          value={formik.values.title}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.title && Boolean(formik.errors.title)}
                          helperText={formik.touched.title && formik.errors.title}
                          required
                       />
                    </Grid>
                    <Grid item xs={12}>
                       <TextField
                          fullWidth
                          id='description'
                          name='description'
                          label='Event Description'
                          multiline
                          rows={4}
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.description && Boolean(formik.errors.description)}
                          helperText={formik.touched.description && formik.errors.description}
                          required
                       />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                       <FormControl
                          fullWidth
                          required
                          error={formik.touched.eventType && Boolean(formik.errors.eventType)}
                       >
                          <InputLabel id='event-type-label'>Event Type</InputLabel>
                          <Select
                             labelId='event-type-label'
                             id='eventType'
                             name='eventType'
                             value={formik.values.eventType}
                             label='Event Type'
                             onChange={formik.handleChange}
                             onBlur={formik.handleBlur}
                          >
                             {eventTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                   {type}
                                </MenuItem>
                             ))}
                          </Select>
                          {formik.touched.eventType && formik.errors.eventType && (
                             <FormHelperText>{formik.errors.eventType}</FormHelperText>
                          )}
                       </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                       <FormControlLabel
                          control={
                             <Switch
                                id='isPublic'
                                name='isPublic'
                                checked={formik.values.isPublic}
                                onChange={formik.handleChange}
                             />
                          }
                          label='Public Event (visible to everyone)'
                       />
                    </Grid>
                    <Grid item xs={12}>
                       <Button
                          variant='outlined'
                          component='label'
                          startIcon={<ImageIcon />}
                          fullWidth
                          sx={{ py: 1.5 }}
                       >
                          Upload Cover Photo
                          <input type='file' hidden accept='image/*' onChange={handleFileChange} />
                       </Button>
                       {coverPhotoPreview && (
                          <Box sx={{ mt: 2, position: 'relative' }}>
                             <img
                                src={coverPhotoPreview}
                                alt='Cover preview'
                                style={{
                                   width: '100%',
                                   borderRadius: '8px',
                                   maxHeight: '200px',
                                   objectFit: 'cover',
                                }}
                             />
                          </Box>
                       )}
                    </Grid>
                 </Grid>

                 <Divider sx={{ my: 2.5 }} />

                 {/* Date & Location Section */}
                 <Typography variant='subtitle1' sx={{ mb: 1.5, fontWeight: 500 }} component='h2'>
                    <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Date & Location
                 </Typography>
                 <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                       <DateTimePicker
                          label='Start Date & Time'
                          value={formik.values.startDate}
                          onChange={(newValue) => formik.setFieldValue('startDate', newValue)}
                          renderInput={(params) => (
                             <TextField
                                {...params}
                                size='small'
                                fullWidth
                                required
                                error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                                helperText={
                                   formik.touched.startDate && (formik.errors.startDate as string)
                                }
                             />
                          )}
                       />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                       <DateTimePicker
                          label='End Date & Time'
                          value={formik.values.endDate}
                          onChange={(newValue) => formik.setFieldValue('endDate', newValue)}
                          renderInput={(params) => (
                             <TextField
                                {...params}
                                size='small'
                                fullWidth
                                required
                                error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                                helperText={
                                   formik.touched.endDate && (formik.errors.endDate as string)
                                }
                             />
                          )}
                       />
                    </Grid>
                    <Grid item xs={12}>
                       <TextField
                          fullWidth
                          id='location'
                          name='location'
                          label='Location'
                          value={formik.values.location}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.location && Boolean(formik.errors.location)}
                          helperText={formik.touched.location && formik.errors.location}
                          required
                          InputProps={{
                             startAdornment: (
                                <InputAdornment position='start'>
                                   <LocationIcon />
                                </InputAdornment>
                             ),
                          }}
                       />
                    </Grid>
                 </Grid>

                 <Divider sx={{ my: 2.5 }} />

                 {/* Ticketing Section */}
                 <Typography variant='subtitle1' sx={{ mb: 1.5, fontWeight: 500 }} component='h2'>
                    <MoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Tickets & Capacity
                 </Typography>
                 <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                       <FormControlLabel
                          control={
                             <Switch
                                id='isPaid'
                                name='isPaid'
                                checked={formik.values.isPaid}
                                onChange={formik.handleChange}
                             />
                          }
                          label='Paid Event'
                       />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                       <TextField
                          fullWidth
                          id='maxCapacity'
                          name='maxCapacity'
                          label='Maximum Capacity'
                          type='number'
                          value={formik.values.maxCapacity}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.maxCapacity && Boolean(formik.errors.maxCapacity)}
                          helperText={formik.touched.maxCapacity && formik.errors.maxCapacity}
                          InputProps={{
                             startAdornment: (
                                <InputAdornment position='start'>
                                   <PeopleIcon />
                                </InputAdornment>
                             ),
                          }}
                       />
                    </Grid>

                    {/* Ticket Types Manager */}
                    {formik.values.isPaid && (
                       <Grid item xs={12}>
                          <Card variant='outlined' sx={{ mb: 2 }}>
                             <CardContent>
                                <Typography
                                   variant='subtitle2'
                                   gutterBottom
                                   sx={{ fontWeight: 500 }}
                                >
                                   Ticket Types
                                </Typography>
                                <TicketTypeManager
                                   ticketTypes={ticketTypes}
                                   onChange={handleTicketTypesChange}
                                   maxCapacity={formik.values.maxCapacity}
                                />
                             </CardContent>
                          </Card>
                       </Grid>
                    )}
                 </Grid>

                 <Divider sx={{ my: 2.5 }} />

                 {/* Participants Section */}
                 <Typography variant='subtitle1' sx={{ mb: 1.5, fontWeight: 500 }} component='h2'>
                    <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Participants
                 </Typography>
                 <Grid container spacing={2}>
                    <Grid item xs={12}>
                       <FormControlLabel
                          control={
                             <Switch
                                id='enableParticipantSharing'
                                name='enableParticipantSharing'
                                checked={formik.values.enableParticipantSharing}
                                onChange={formik.handleChange}
                             />
                          }
                          label='Enable Participant Collaboration'
                       />
                       <Typography variant='body2' color='text.secondary' sx={{ mt: 1, mb: 2 }}>
                          Control how participants interact with each other during your event.
                       </Typography>

                       <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant='body2' sx={{ color: 'info.contrastText' }}>
                             <strong>How this works:</strong>
                             <br />• <strong>When enabled:</strong> All invited participants can see
                             each other and have the option to add others to their network
                             <br />• <strong>When disabled:</strong> Only event owners and editors
                             can see the full participant list. Regular participants won't see other
                             attendees
                          </Typography>
                       </Box>
                    </Grid>
                 </Grid>

                 <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                       variant='outlined'
                       sx={{ mr: 2 }}
                       onClick={() => navigate(`/organizations/${id}/events`)}
                    >
                       Cancel
                    </Button>
                    <Button
                       type='submit'
                       variant='contained'
                       color='primary'
                       disabled={loading}
                       startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                       {loading ? 'Creating...' : 'Create Event'}
                    </Button>
                 </Box>
              </form>
              {/* {eventId && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Event Embed Code
              </Typography>
              <EventEmbedGenerator 
                eventId={eventId} 
                publicLinkId={undefined} 
                organizationId={id || ''} 
                eventTitle={formik.values.title}
              />
            </Box>
          )} */}
           </Paper>
        </Container>
     </LocalizationProvider>
  );
};

export default CreateOrgEvent;